import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import { useMemo } from "react";

import { diffLines } from "./diff-lines";
import { shortRevisionId } from "./short-revision-id";
import type { WikiRevision } from "../wiki-types";

export function WikiRevisionDiff({
  older,
  newer,
}: Readonly<{
  older: WikiRevision;
  newer: WikiRevision;
}>) {
  const rows = useMemo(
    () =>
      diffLines({
        oldLines: older.body.split("\n"),
        newLines: newer.body.split("\n"),
      }),
    [older.body, newer.body],
  );
  const added = rows.filter((row) => row.type === "added").length;
  const removed = rows.filter((row) => row.type === "removed").length;
  return (
    <section aria-label="Revision diff" {...stylex.props(styles.container)}>
      <h3 {...stylex.props(styles.heading)}>
        {shortRevisionId(older.id)} → {shortRevisionId(newer.id)} · {added}{" "}
        {added === 1 ? "line" : "lines"} added · {removed} removed
      </h3>
      <div {...stylex.props(styles.lines)}>
        {rows.map((row, i) => (
          <div key={i} {...stylex.props(styles.line, styles[row.type])}>
            <span {...stylex.props(styles.marker)}>
              {row.type === "added" ? "+" : row.type === "removed" ? "−" : " "}
            </span>
            <span {...stylex.props(styles.content)}>
              {row.text.length ? row.text : " "}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

const styles = stylex.create({
  container: {
    marginTop: spacing.space16,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.medium,
    overflow: "hidden",
  },
  heading: {
    margin: 0,
    padding: spacing.space12,
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderBottomColor: colors.border,
    backgroundColor: colors.surfaceAlt,
    fontFamily: fonts.sans,
    fontSize: fontSizes.caption,
    color: colors.muted,
    fontWeight: 400,
  },
  lines: {
    maxHeight: "24rem",
    overflow: "auto",
    fontFamily: fonts.mono,
    fontSize: fontSizes.caption,
    lineHeight: 1.7,
  },
  line: {
    display: "flex",
    gap: spacing.space8,
    paddingInline: spacing.space12,
    paddingBlock: spacing.space2,
  },
  marker: { width: "1ch", flexShrink: 0, userSelect: "none" },
  content: { whiteSpace: "pre-wrap", overflowWrap: "anywhere", minWidth: 0 },
  added: { color: colors.chartGreen, backgroundColor: colors.surfaceAlt },
  removed: { color: colors.danger, backgroundColor: colors.dangerSurface },
  unchanged: { color: colors.subtle },
});
